/**
 * useHealthSummary - Hook for fetching health summary data
 *
 * Task 060a: Provides health summary for Admin Console heads-up display
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useNotification } from '@/stores/notificationStore';
import { HttpAdminAdapter } from '@/services/admin/HttpAdminAdapter';
import type { HealthSummary } from '@/services/admin/AdminService';

// Auto-refresh interval in milliseconds (60 seconds)
const AUTO_REFRESH_INTERVAL = 60000;

/**
 * Hook for fetching and managing health summary data
 */
export const useHealthSummary = (autoRefresh: boolean = true) => {
  const [adminService] = useState(() => new HttpAdminAdapter());
  const [healthSummary, setHealthSummary] = useState<HealthSummary | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showNotification } = useNotification();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchHealthSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const summary = await adminService.getHealthSummary();
      setHealthSummary(summary);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch health summary';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminService]);

  const refreshHealthSummary = useCallback(() => {
    return fetchHealthSummary();
  }, [fetchHealthSummary]);

  // Initial fetch
  useEffect(() => {
    fetchHealthSummary();
  }, [fetchHealthSummary]);

  // Auto-refresh setup
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchHealthSummary();
      }, AUTO_REFRESH_INTERVAL);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, fetchHealthSummary]);

  return {
    healthSummary,
    loading,
    error,
    fetchHealthSummary,
    refreshHealthSummary,
  };
};

export default useHealthSummary;
